import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, useState } from 'react';
import { usePopper } from 'react-popper';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { NavLink } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { search } from 'flow/actions/app-actions';
import SearchBar from 'flow/components/SearchBar';
import Icon from 'flow/components/ui-components/Icon';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { routes } from 'flow/types/routes';
import ChatSessionsList from './ChatSessionsList';
/* The chat list is virtualised, so it renders the same handful of rows however
 * many chats match. That leaves the wait free to sit under a typing interval,
 * where results track the field instead of arriving after it. */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
var CHAT_SEARCH_DEBOUNCE_MS = 300;
var Sessions = function Sessions() {
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    extensionFilter = _useState2[0],
    setExtensionFilter = _useState2[1];
  var dispatch = useAppDispatch();

  /* SearchBar hands the text over already debounced, so this dispatches as
   * it arrives - waiting again only adds to the delay before the list
   * catches up with the field. */
  var handleFilter = function handleFilter(searchText) {
    setExtensionFilter(searchText);
    dispatch(search(searchText));
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "flex h-full flex-1 flex-col border-e bg-gray-100",
    children: [/*#__PURE__*/_jsx("div", {
      className: "p-3",
      children: /*#__PURE__*/_jsx(SearchBar, {
        onChange: handleFilter,
        initialSearchText: extensionFilter,
        debounceMs: CHAT_SEARCH_DEBOUNCE_MS,
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(NewChatButton, {}), /*#__PURE__*/_jsx(ActionButton, {})]
        })
      })
    }), /*#__PURE__*/_jsx(ChatSessionsList, {})]
  });
};
var NewChatButton = function NewChatButton() {
  var _useAppContext = useAppContext(),
    i18next = _useAppContext.i18next;
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    referenceElement = _useState4[0],
    setReferenceElement = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    popperElement = _useState6[0],
    setPopperElement = _useState6[1];
  var _usePopper = usePopper(referenceElement, popperElement, {
      placement: 'bottom-start',
      modifiers: [{
        name: 'flip',
        enabled: true,
        options: {
          flipVariations: true,
          allowedAutoPlacements: ['bottom-start', 'bottom-end']
        }
      }, {
        name: 'preventOverflow',
        enabled: true
      }]
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes;
  return /*#__PURE__*/_jsx(Menu, {
    as: "div",
    children: function children(_ref) {
      var open = _ref.open;
      return /*#__PURE__*/_jsxs("div", {
        className: "relative",
        children: [/*#__PURE__*/_jsxs(MenuButton, {
          ref: setReferenceElement,
          className: "size-10 items-center rounded-sm bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-700",
          children: [/*#__PURE__*/_jsx("span", {
            className: "sr-only",
            children: i18next.t('chat.new_chat')
          }), /*#__PURE__*/_jsx(Icon, {
            iconKey: "add"
          })]
        }), /*#__PURE__*/_jsx(Transition, {
          show: open,
          as: Fragment,
          leave: "transition ease-in duration-100",
          leaveFrom: "opacity-100",
          leaveTo: "opacity-0",
          children: /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
            ref: setPopperElement,
            style: styles.popper
          }, attributes.popper), {}, {
            className: "shadow-base start-0 top-11 z-30 mt-1 flex w-max flex-col gap-4 rounded-sm bg-white p-3 text-gray-800",
            children: /*#__PURE__*/_jsx(MenuItems, {
              as: "div",
              className: "flex max-h-[520px] origin-bottom-right flex-col gap-2 overflow-hidden outline-hidden rtl:origin-top-left",
              children: /*#__PURE__*/_jsx(MenuItem, {
                children: /*#__PURE__*/_jsx(NavLink, {
                  to: routes['messages.create'],
                  children: /*#__PURE__*/_jsxs("button", {
                    className: "group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200",
                    children: [/*#__PURE__*/_jsx(Icon, {
                      iconKey: "message",
                      className: "text-gray-600 group-hover:text-gray-700"
                    }), /*#__PURE__*/_jsx("p", {
                      children: i18next.t('chat.new_chat')
                    })]
                  })
                })
              })
            })
          }))
        })]
      });
    }
  });
};
var ActionButton = function ActionButton() {
  var _useAppContext2 = useAppContext(),
    i18next = _useAppContext2.i18next;
  var _useModal = useModal(),
    openModal = _useModal.openModal;
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    referenceElement = _useState8[0],
    setReferenceElement = _useState8[1];
  var _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    popperElement = _useState0[0],
    setPopperElement = _useState0[1];
  var _usePopper2 = usePopper(referenceElement, popperElement, {
      placement: 'bottom-start',
      modifiers: [{
        name: 'flip',
        enabled: true,
        options: {
          flipVariations: true,
          allowedAutoPlacements: ['bottom-start', 'bottom-end']
        }
      }, {
        name: 'preventOverflow',
        enabled: true
      }]
    }),
    styles = _usePopper2.styles,
    attributes = _usePopper2.attributes;
  return /*#__PURE__*/_jsx(Menu, {
    as: "div",
    children: function children(_ref2) {
      var open = _ref2.open;
      return /*#__PURE__*/_jsxs("div", {
        className: "relative",
        children: [/*#__PURE__*/_jsxs(MenuButton, {
          ref: setReferenceElement,
          className: "size-10 items-center rounded-sm bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-700",
          children: [/*#__PURE__*/_jsx("span", {
            className: "sr-only",
            children: i18next.t('settings.more_options')
          }), /*#__PURE__*/_jsx(Icon, {
            iconKey: "more_vert"
          })]
        }), /*#__PURE__*/_jsx(Transition, {
          show: open,
          as: Fragment,
          leave: "transition ease-in duration-100",
          leaveFrom: "opacity-100",
          leaveTo: "opacity-0",
          children: /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
            ref: setPopperElement,
            style: styles.popper
          }, attributes.popper), {}, {
            className: "shadow-base start-0 top-11 z-30 mt-1 flex w-max flex-col gap-4 rounded-sm bg-white p-2 text-gray-800",
            children: /*#__PURE__*/_jsxs(MenuItems, {
              as: "div",
              className: "max-h-[520px] origin-bottom-right overflow-hidden outline-hidden rtl:origin-top-left",
              children: [/*#__PURE__*/_jsx(MenuItem, {
                children: /*#__PURE__*/_jsxs("button", {
                  className: "group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200",
                  onClick: function onClick() {
                    return openModal('listRooms');
                  },
                  children: [/*#__PURE__*/_jsx(Icon, {
                    iconKey: "public",
                    className: "text-gray-600 group-hover:text-gray-700"
                  }), /*#__PURE__*/_jsx("p", {
                    children: i18next.t('Chat.Create.Alert.Public')
                  })]
                })
              }, "list-rooms-modal"), /*#__PURE__*/_jsx(MenuItem, {
                children: /*#__PURE__*/_jsxs("button", {
                  className: "group flex w-full items-center gap-x-2 rounded-sm p-2 hover:bg-gray-200",
                  onClick: function onClick() {
                    return openModal('archivedChats');
                  },
                  children: [/*#__PURE__*/_jsx(Icon, {
                    iconKey: "archive",
                    className: "text-gray-600 group-hover:text-gray-700"
                  }), /*#__PURE__*/_jsx("p", {
                    children: i18next.t('chat.archived.title')
                  })]
                })
              }, "archived-rooms")]
            })
          }))
        })]
      });
    }
  });
};
export default Sessions;